// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { Dataset } from '../data';
import type { ViewId } from '../types';
import {
  materialsForYear, statesForYear, materialSeries,
  filterRows, total, recoveryRate, sumBy,
} from '../data';
import { catInfo, jurisName, jurisColour, streamInfo, FATES, isRecovery } from '../domain';
import { lineChartSvg, stackedBarHtml, legendHtml, type Seg } from './charts';
import { escapeHtml, formatTonnes, formatPercent, fyShort } from '../utils/format';

type ViewNav = (view: ViewId) => void;

export function createDrawers(data: Dataset, goTo: ViewNav) {
  const overlay = document.createElement('div');
  overlay.className = 'overlay';
  const drawer = document.createElement('div');
  drawer.className = 'drawer';
  drawer.setAttribute('role', 'dialog');
  drawer.setAttribute('aria-modal', 'true');
  const closeBtn = document.createElement('button');
  closeBtn.className = 'modal-close';
  closeBtn.setAttribute('aria-label', 'Close');
  closeBtn.textContent = '✕';

  let mounted = false;
  let removeTimer: number | undefined;

  const mount = () => {
    if (mounted) return;
    window.clearTimeout(removeTimer);
    document.body.append(overlay, drawer);
    void drawer.offsetWidth;
    mounted = true;
  };
  const close = () => {
    overlay.classList.remove('open');
    drawer.classList.remove('open');
    if (location.hash) history.replaceState(null, '', location.pathname + location.search);
    removeTimer = window.setTimeout(() => { overlay.remove(); drawer.remove(); mounted = false; }, 300);
  };
  const openHtml = (hash: string, head: string, body: string, after?: (root: HTMLElement) => void) => {
    mount();
    drawer.innerHTML = `<div class="drawer-head">${head}</div><div class="drawer-body">${body}</div>`;
    drawer.appendChild(closeBtn);
    overlay.classList.add('open');
    drawer.classList.add('open');
    drawer.scrollTop = 0;
    closeBtn.focus();
    if (hash) history.replaceState(null, '', `#${hash}`);
    after?.(drawer);
  };
  overlay.addEventListener('click', close);
  closeBtn.addEventListener('click', close);
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && mounted && drawer.classList.contains('open')) close(); });

  const y = data.latestYear;

  function fateSegs(byFate: Map<string, number>): Seg[] {
    return FATES.map((f) => ({ value: byFate.get(f.key) || 0, colour: f.colour, label: f.label, tip: `${f.label}: ${formatTonnes(byFate.get(f.key) || 0)}` }));
  }
  function miniRanked(items: Array<{ label: string; value: number; colour: string; sub?: string; data?: Record<string, string> }>, fmt: (v: number) => string): string {
    if (!items.length) return '<p class="empty" style="padding:var(--space-md)">No data.</p>';
    const max = Math.max(...items.map((i) => i.value), 1);
    return `<div class="mini-ranked">${items
      .map((i) => {
        const attrs = i.data ? Object.entries(i.data).map(([k, v]) => `data-${k}="${escapeHtml(v)}"`).join(' ') : '';
        return `<div class="mr-row" ${attrs}${i.data ? ' role="button" style="cursor:pointer"' : ''}><span class="mr-label">${escapeHtml(i.label)}</span><span class="mr-track"><span class="mr-fill" style="width:${((i.value / max) * 100).toFixed(1)}%;background:${i.colour}"></span></span><span class="mr-val">${escapeHtml(fmt(i.value))}</span></div>`;
      })
      .join('')}</div>`;
  }

  // ── Material ────────────────────────────────────────────────────────────
  function openMaterial(key: string): void {
    const info = catInfo(key);
    const mats = materialsForYear(data, y);
    const me = mats.find((m) => m.key === key);
    if (!me) return;
    const rankTon = mats.findIndex((m) => m.key === key) + 1;
    const byRate = [...mats].sort((a, b) => b.rate - a.rate);
    const rankRate = byRate.findIndex((m) => m.key === key) + 1;
    const meanRate = mats.reduce((a, m) => a + m.rate, 0) / (mats.length || 1);
    const series = materialSeries(data, key);
    // where by state
    const byState = data.jurisCodes
      .map((code) => ({ code, t: total(filterRows(data.rows, { yr: y, c: key, j: code })) }))
      .filter((s) => s.t > 0)
      .sort((a, b) => b.t - a.t)
      .slice(0, 8);

    const head = `
      <div class="drawer-eyebrow" style="color:${info.colour}">Material</div>
      <div class="drawer-title" style="font-family:var(--font-sans)">${escapeHtml(info.short)}</div>
      <div class="drawer-sub">${formatTonnes(me.total)} in ${escapeHtml(fyShort(y))} · ${formatPercent(me.rate, 0)} recovered</div>`;

    const body = `
      <div class="drawer-section"><p class="drawer-note">${escapeHtml(info.note)}</p></div>
      <div class="drawer-section">
        <div class="kv-grid">
          <div class="kv"><div class="kv-label">Total waste</div><div class="kv-value" style="font-size:var(--font-size-lg)">${formatTonnes(me.total)}</div></div>
          <div class="kv"><div class="kv-label">Recovery rate</div><div class="kv-value" style="font-size:var(--font-size-lg);color:${me.rate >= meanRate ? 'var(--status-good)' : 'var(--status-bad)'}">${formatPercent(me.rate, 0)}</div></div>
          <div class="kv"><div class="kv-label">Rank by volume</div><div class="kv-value" style="font-size:var(--font-size-lg)">#${rankTon}</div></div>
          <div class="kv"><div class="kv-label">Rank by recovery</div><div class="kv-value" style="font-size:var(--font-size-lg)">#${rankRate}</div></div>
        </div>
        <p class="drawer-note" style="margin-top:var(--space-sm)">${me.rate >= meanRate ? 'Above' : 'Below'} the ${formatPercent(meanRate, 0)} average recovery rate across all materials.</p>
      </div>
      <div class="drawer-section"><h3>Where it ends up (${escapeHtml(fyShort(y))})</h3>${stackedBarHtml(fateSegs(me.byFate), { tall: true })}${legendHtml(FATES.filter((f) => (me.byFate.get(f.key) || 0) > 0).map((f) => ({ colour: f.colour, label: f.label })))}</div>
      <div class="drawer-section"><h3>Recovery rate over time</h3>${lineChartSvg(
        [{ label: 'Recovery rate', colour: info.colour, points: series.map((s, i) => ({ x: i, y: s.total > 0 ? s.rate : null })) }],
        { width: 420, height: 170, xLabels: data.years.map(fyShort), yMax: 100, yFmt: (v) => `${v.toFixed(0)}%`, markers: true },
      )}</div>
      <div class="drawer-section"><h3>Which states discard the most</h3>${miniRanked(byState.map((s) => ({ label: jurisName(s.code), value: s.t, colour: jurisColour(s.code), data: { 'open-state': s.code } })), formatTonnes)}</div>
      <div class="drawer-section">
        <button class="chip" data-goto="materials">Compare all materials →</button>
        <button class="chip" data-goto="flow">See the flow →</button>
      </div>`;

    openHtml(`m=${key}`, head, body, wireLinks);
  }

  // ── State ───────────────────────────────────────────────────────────────
  function openState(code: string): void {
    const states = statesForYear(data, y);
    const me = states.find((s) => s.code === code);
    if (!me || me.total === 0) return;
    const rankTon = [...states].sort((a, b) => b.total - a.total).findIndex((s) => s.code === code) + 1;
    const rankRate = [...states].sort((a, b) => b.rate - a.rate).findIndex((s) => s.code === code) + 1;
    const natRate = recoveryRate(filterRows(data.rows, { yr: y }));
    const rows = filterRows(data.rows, { yr: y, j: code });
    const byFate = sumBy(rows, (r) => r.f);
    const byCat = data.catKeys
      .map((k) => ({ key: k, t: total(filterRows(data.rows, { yr: y, c: k, j: code })) }))
      .filter((c) => c.t > 0)
      .sort((a, b) => b.t - a.t)
      .slice(0, 8);
    const streams = data.streamKeys.map((s) => {
      const sr = data.streamRows.filter((r) => r.yr === y && r.j === code && r.s === s);
      const t = sr.reduce((a, r) => a + r.t, 0);
      const rec = sr.reduce((a, r) => a + (isRecovery(r.f) ? r.t : 0), 0);
      return { key: s, t, rate: t ? (rec / t) * 100 : 0 };
    }).filter((s) => s.t > 0);
    const series = data.years.map((yr) => total(filterRows(data.rows, { yr, j: code })));

    const head = `
      <div class="drawer-eyebrow" style="color:${jurisColour(code)}">State / territory</div>
      <div class="drawer-title" style="font-family:var(--font-sans)">${escapeHtml(jurisName(code))}</div>
      <div class="drawer-sub">${formatTonnes(me.total)} · ${formatPercent(me.rate, 0)} recovered · ${me.perCapita != null ? `${me.perCapita.toFixed(1)} t/person` : '—'}</div>`;

    const body = `
      <div class="drawer-section">
        <div class="kv-grid">
          <div class="kv"><div class="kv-label">Total waste</div><div class="kv-value" style="font-size:var(--font-size-lg)">${formatTonnes(me.total)}</div></div>
          <div class="kv"><div class="kv-label">Recovery rate</div><div class="kv-value" style="font-size:var(--font-size-lg);color:${me.rate >= natRate ? 'var(--status-good)' : 'var(--status-bad)'}">${formatPercent(me.rate, 0)}</div></div>
          <div class="kv"><div class="kv-label">Per person</div><div class="kv-value" style="font-size:var(--font-size-lg)">${me.perCapita != null ? `${me.perCapita.toFixed(1)} t` : '—'}</div></div>
          <div class="kv"><div class="kv-label">Rank (volume · recovery)</div><div class="kv-value" style="font-size:var(--font-size-lg)">#${rankTon} · #${rankRate}</div></div>
        </div>
        <p class="drawer-note" style="margin-top:var(--space-sm)">Recovery is ${me.rate >= natRate ? 'above' : 'below'} the national ${formatPercent(natRate, 0)}.</p>
      </div>
      <div class="drawer-section"><h3>Where it ends up</h3>${stackedBarHtml(fateSegs(byFate), { tall: true })}${legendHtml(FATES.filter((f) => (byFate.get(f.key) || 0) > 0).map((f) => ({ colour: f.colour, label: f.label })))}</div>
      <div class="drawer-section"><h3>By stream</h3>${miniRanked(streams.map((s) => ({ label: `${streamInfo(s.key).short} · ${formatPercent(s.rate, 0)} recovered`, value: s.t, colour: streamInfo(s.key).colour })), formatTonnes)}</div>
      <div class="drawer-section"><h3>Top materials</h3>${miniRanked(byCat.map((c) => ({ label: catInfo(c.key).short, value: c.t, colour: catInfo(c.key).colour, data: { 'open-material': c.key } })), formatTonnes)}</div>
      <div class="drawer-section"><h3>Total waste over time</h3>${lineChartSvg(
        [{ label: 'Total', colour: jurisColour(code), points: series.map((t, i) => ({ x: i, y: t / 1e6 })) }],
        { width: 420, height: 170, xLabels: data.years.map(fyShort), yFmt: (v) => `${v.toFixed(0)}Mt`, markers: true },
      )}</div>
      <div class="drawer-section"><button class="chip" data-goto="map">Back to the map →</button></div>`;

    openHtml(`s=${code}`, head, body, wireLinks);
  }

  function wireLinks(root: HTMLElement): void {
    root.querySelectorAll('[data-open-material]').forEach((b) => b.addEventListener('click', () => openMaterial(b.getAttribute('data-open-material') as string)));
    root.querySelectorAll('[data-open-state]').forEach((b) => b.addEventListener('click', () => openState(b.getAttribute('data-open-state') as string)));
    root.querySelectorAll('[data-goto]').forEach((b) => b.addEventListener('click', () => { close(); goTo(b.getAttribute('data-goto') as ViewId); }));
  }

  return { openMaterial, openState, close };
}
